// src/components/common/Modal.js
import React from 'react';
import { CSSTransition } from 'react-transition-group';

// Make sure to install react-transition-group:
// npm install react-transition-group
// yarn add react-transition-group

const Modal = ({ isOpen, onClose, title, children, size = 'xl' }) => {
  const nodeRef = React.useRef(null);

  const sizeClasses = {
    sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-xl',
    '2xl': 'max-w-2xl', '3xl': 'max-w-3xl', '4xl': 'max-w-4xl', '5xl': 'max-w-5xl',
  };

  // Handle Escape key press
  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);


  return (
    <CSSTransition
      in={isOpen}
      timeout={300}
      classNames="modal-fade" // Ensure CSS classes are defined
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div
        ref={nodeRef}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" onClick={onClose} aria-hidden="true"></div>

        {/* Modal Panel */}
        <div className={`relative bg-white rounded-lg shadow-xl overflow-hidden transform transition-all w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col`}>
          {/* Modal Header */}
          {(title || onClose) && ( // Render header if title or close button is needed
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              {title ? (
                <h2 id="modal-title" className="text-lg font-medium text-gray-900">{title}</h2>
              ) : <div />} {/* Placeholder to keep close button aligned right */}
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              )}
            </div>
          )}

          {/* Modal Body (Scrollable) */}
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

// Add corresponding CSS for the transition (e.g., in your index.css or App.css)
/*
.modal-fade-enter {
  opacity: 0;
  transform: scale(0.95);
}
.modal-fade-enter-active {
  opacity: 1;
  transform: scale(1);
  transition: opacity 300ms ease-out, transform 300ms ease-out;
}
.modal-fade-exit {
  opacity: 1;
  transform: scale(1);
}
.modal-fade-exit-active {
  opacity: 0;
  transform: scale(0.95);
  transition: opacity 300ms ease-in, transform 300ms ease-in;
}
*/

export default Modal;
